import React from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import "./styles/index.css";

import { ThemeProvider } from "./context/ThemeContext";
import { AuthProvider, useAuth } from "./context/AuthContext";

import Login from "./pages/auth/Login";
import Setup from "./pages/auth/Setup";
import UserValidation from "./components/UserValidation";
import Forbidden from "./pages/common/Forbidden";

import PermissionRoute from "./components/PermissionRoute";
import { Permission } from "./permissions/permission.enum";
import CardInterestsList from "./pages/card-interests/CardInterestsList";
import CardInterestDetail from "./pages/card-interests/CardInterestDetail";
import ChannelConfigPage from "./pages/channel-config/ChannelConfigPage";
import FacebookListingsPage from "./pages/listings/FacebookListingsPage";
import WhatsappListingsPage from "./pages/listings/WhatsappListingsPage";

const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { isAuthenticated, loading } = useAuth();
  const { t } = useTranslation("common");

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center">
        {t("loading")}
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
};

function App() {
  return (
    <HelmetProvider>
      <ThemeProvider>
        <AuthProvider>
          <Router>
            <UserValidation />
            <div className="min-h-screen bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-gray-100 transition-colors duration-200">
              <Routes>
                <Route path="/login" element={<Login />} />
                <Route path="/setup" element={<Setup />} />
                <Route path="/forbidden" element={<Forbidden />} />

                <Route
                  path="/"
                  element={
                    <ProtectedRoute>
                      <Navigate to="/card-interests" replace />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/card-interests"
                  element={
                    <ProtectedRoute>
                      <PermissionRoute
                        permission={Permission.CARD_INTERESTS_READ}
                      >
                        <CardInterestsList />
                      </PermissionRoute>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/card-interests/:id"
                  element={
                    <ProtectedRoute>
                      <PermissionRoute
                        permission={Permission.CARD_INTERESTS_READ}
                      >
                        <CardInterestDetail />
                      </PermissionRoute>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/channel-config"
                  element={
                    <ProtectedRoute>
                      <PermissionRoute
                        permission={Permission.CARD_INTERESTS_UPDATE}
                      >
                        <ChannelConfigPage />
                      </PermissionRoute>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/whatsapp-listings"
                  element={
                    <ProtectedRoute>
                      <PermissionRoute permission={Permission.LISTINGS_READ}>
                        <WhatsappListingsPage />
                      </PermissionRoute>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/facebook-listings"
                  element={
                    <ProtectedRoute>
                      <PermissionRoute permission={Permission.LISTINGS_READ}>
                        <FacebookListingsPage />
                      </PermissionRoute>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="*"
                  element={<Navigate to="/card-interests" replace />}
                />
              </Routes>
            </div>
          </Router>
        </AuthProvider>
      </ThemeProvider>
    </HelmetProvider>
  );
}

export default App;
